import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { FadeUp, PageHero } from "@/components/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CATEGORIES = ["Tutte", "Creator", "Privacy", "Contratto", "Guadagni"];

const FAQS = [
  { cat: "Creator", q: "Cosa fa esattamente IEM per un creator?", a: "Gestiamo strategia, contenuti, vendite, crescita e protezione del brand. Tu ti concentri sulla creazione: il sistema operativo è interamente gestito dal nostro team." },
  { cat: "Creator", q: "Lavorate solo con OnlyFans?", a: "OnlyFans è uno dei principali ecosistemi di monetizzazione in cui operiamo, ma il nostro lavoro è costruire un brand: social, membership, collaborazioni e altre fonti di ricavo." },
  { cat: "Creator", q: "Devo già avere un seguito per candidarmi?", a: "No. Valutiamo il potenziale, la costanza e la motivazione, non solo i numeri attuali. Ogni candidatura viene analizzata individualmente." },
  { cat: "Privacy", q: "La mia identità sarà protetta?", a: "Sì. Ogni collaborazione è coperta da NDA, gli accessi sono riservati e possiamo operare mantenendo separata la tua identità personale da quella pubblica." },
  { cat: "Privacy", q: "Chi ha accesso al mio account?", a: "Solo il personale autorizzato e formato, con credenziali gestite tramite sistemi sicuri. Ogni accesso è tracciato." },
  { cat: "Privacy", q: "I miei contenuti sono protetti dalla diffusione non autorizzata?", a: "Monitoriamo la diffusione non autorizzata dei contenuti e interveniamo con procedure di rimozione dedicate." },
  { cat: "Contratto", q: "Quanto dura la collaborazione?", a: "I termini vengono definiti insieme in fase di onboarding, con accordi scritti e trasparenti. Nessun vincolo nascosto." },
  { cat: "Contratto", q: "Come funziona la candidatura?", a: "Ci scrivi su WhatsApp, fissiamo una call conoscitiva e valutiamo insieme obiettivi e potenziale. Riservatezza garantita dal primo messaggio." },
  { cat: "Guadagni", q: "Come viene remunerata l'agenzia?", a: "Lavoriamo con un modello a percentuale sui risultati generati: guadagniamo solo se cresci tu. I dettagli vengono definiti in modo trasparente prima di iniziare." },
  { cat: "Guadagni", q: "Quanto si può guadagnare?", a: "Dipende da potenziale, nicchia e costanza. Non promettiamo cifre inventate: in fase di call analizziamo il tuo caso concreto con dati reali." },
];

export default function Faq() {
  const { openContact } = useSite();
  const [cat, setCat] = useState("Tutte");
  const filtered = cat === "Tutte" ? FAQS : FAQS.filter((f) => f.cat === cat);

  return (
    <main data-testid="faq-page">
      <PageHero
        testid="faq-hero"
        eyebrow="FAQ"
        lines={[
          "Domande frequenti.",
          <span key="l2" className="gold-text gold-text-animated italic">Risposte chiare.</span>,
        ]}
        sub="Trasparenza prima di tutto. Se non trovi la risposta che cerchi, scrivici direttamente."
      />

      <section className="pb-16 lg:pb-24" data-testid="faq-list-section">
        <div className="mx-auto max-w-4xl px-6">
          <FadeUp className="flex flex-wrap gap-2" >
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`faq-filter-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  cat === c
                    ? "border-gold bg-gold/10 text-gold"
                    : "border-[#1A1A1A] text-[#969696] hover:border-gold/40 hover:text-[#F5F3EE]"
                }`}
              >
                {c}
              </button>
            ))}
          </FadeUp>

          <FadeUp delay={0.1} className="mt-10">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {filtered.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-[#1A1A1A]">
                  <AccordionTrigger
                    data-testid={`faq-question-${i}`}
                    className="py-6 text-left font-serif text-lg sm:text-xl text-[#F5F3EE] hover:text-gold hover:no-underline"
                  >
                    <span className="flex items-baseline gap-4">
                      <span className="text-[10px] font-sans font-semibold uppercase tracking-[0.2em] text-gold/70">
                        {f.cat}
                      </span>
                      {f.q}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-answer-${i}`}
                    className="pb-6 text-sm leading-relaxed text-[#969696]"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-14 border border-[#1A1A1A] bg-[#0d0d0d] p-8 text-center lg:p-12">
            <h3 className="font-serif text-2xl sm:text-3xl text-[#F5F3EE]">
              Non trovi la risposta? <span className="italic text-gold">Parliamone.</span>
            </h3>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-[#969696]">
              Ogni conversazione con il nostro team è riservata e senza impegno.
            </p>
            <button
              data-testid="faq-contact-cta"
              onClick={() => openContact("creator")}
              className="mt-8 inline-flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-bold tracking-[0.18em] text-[#080808] transition-all duration-300 hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(229,196,90,0.25)]"
            >
              <MessageCircle size={16} /> CONTATTACI
            </button>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
